import React, { useRef } from 'react';
import config from '../config';
import { useAlgo } from '../hooks/useAlgo';
import { useDonationSelection } from '../hooks/useDonations';
import Button from './Button';
import ConnectWalletMessage from './ConnectWalletMessage';
import AssetSelector from './donations/AssetSelector';
import DonationsFeed from './donations/DonationsFeed';
import Spinner from './Spinner';

const DonationsPage = () => {
  const { address } = useAlgo();

  if (!config.donationConfig.enabled) {
    return (
      <div className='text-center py-20'>
        <p className='text-gray-400'>The donations feature is currently disabled.</p>
      </div>
    );
  }

  if (!address) {
    return (
      <div className='text-center py-20'>
        <div className='max-w-md mx-auto'>
          <ConnectWalletMessage />
        </div>
      </div>
    );
  }

  return (
    <div className='pb-32'>
      <DonationsPageInner />
      <DonationsFeed />
    </div>
  );
};

const DonationsPageInner = () => {
  const confettiRef = useRef();
  const { donatableAssets, selectedAssets, canDonate, donationMutate, toggleAssetSelection, clearSelection, isLoading } = useDonationSelection();

  const handleDonate = async () => {
    try {
      await donationMutate.mutateAsync({}, { context: { confettiRef } });
    } catch (error) {
      console.error('Donation failed:', error);
    }
  };

  return (
    <div className='py-8'>
      <div className='text-center mb-12'>
        <div className='bg-gray-900 rounded-lg p-6 max-w-2xl mx-auto'>
          <h2 className='text-xl font-semibold text-white mb-4'>How it works</h2>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 text-sm'>
            <div className='text-center'>
              <div className='text-2xl mb-2'>🎯</div>
              <p className='text-gray-300'>Select NFTs from your wallet to donate</p>
            </div>
            <div className='text-center'>
              <div className='text-2xl mb-2'>🏛️</div>
              <p className='text-gray-300'>NFTs are transferred to the Shitty Vault</p>
            </div>
            <div className='text-center'>
              <div className='text-2xl mb-2'>🎉</div>
              <p className='text-gray-300'>Community benefits from expanded rewards pool</p>
            </div>
          </div>
        </div>
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-4 gap-8'>
        <div className='xl:col-span-3'>
          <div className='mb-6'>
            <h2 className='text-2xl font-bold text-white mb-4'>Select NFTs to Donate</h2>
            <p className='text-gray-400 mb-4'>Choose up to {config.donationConfig.maxAssetsPerDonation} NFTs from your wallet.</p>
          </div>

          <AssetSelector assets={donatableAssets} selectedAssets={selectedAssets} onToggleAsset={toggleAssetSelection} isLoading={isLoading} />
        </div>

        <div className='xl:col-span-1'>
          <div className='sticky top-8'>
            <DonationSummary
              selectedAssets={selectedAssets || []}
              canDonate={canDonate}
              isLoading={donationMutate?.isLoading}
              onDonate={handleDonate}
              onClearSelection={clearSelection}
            />
          </div>
        </div>
      </div>

      <div ref={confettiRef}></div>
    </div>
  );
};

const DonationSummary = ({ selectedAssets, canDonate, isLoading, onDonate, onClearSelection }) => {
  return (
    <div className='bg-gray-900 rounded-lg p-6'>
      <h3 className='text-xl font-bold text-white mb-4'>Donation Summary</h3>

      <div className='space-y-4'>
        <div className='flex justify-between items-center'>
          <span className='text-gray-400'>Selected NFTs:</span>
          <span className='text-white font-semibold'>{selectedAssets.length}</span>
        </div>

        <div className='flex justify-between items-center'>
          <span className='text-gray-400'>Max allowed:</span>
          <span className='text-white'>{config.donationConfig.maxAssetsPerDonation}</span>
        </div>

        <div className='flex justify-between items-center'>
          <span className='text-gray-400'>Destination:</span>
          <span className='text-white text-sm'>{config.donationConfig.destination.name}</span>
        </div>

        {selectedAssets.length > 0 && (
          <div className='pt-4 border-t border-gray-700'>
            <h4 className='text-sm font-medium text-gray-300 mb-2'>Selected Assets:</h4>
            <div className='space-y-1 max-h-40 overflow-y-auto'>
              {selectedAssets.map((asset) => (
                <div key={asset['asset-id']} className='text-xs text-gray-400 truncate'>
                  {asset.params?.name || `Asset ${asset['asset-id']}`}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className='pt-4 space-y-2'>
          <Button theme='primary' onClick={onDonate} disabled={!canDonate || isLoading} fullWidth className='!px-0 w-full'>
            {isLoading ? (
              <div className='flex items-center justify-center gap-2'>
                <Spinner dimensionClasses='w-4 h-4' />
                <span>Processing Donation...</span>
              </div>
            ) : selectedAssets.length === 0 ? (
              'Select NFTs to Donate'
            ) : (
              `Donate ${selectedAssets.length} NFT${selectedAssets.length !== 1 ? 's' : ''}`
            )}
          </Button>

          {selectedAssets.length > 0 && (
            <Button theme='secondary' onClick={onClearSelection} disabled={isLoading} fullWidth className='!px-0 w-full'>
              Clear Selection
            </Button>
          )}
        </div>

        <div className='pt-4 border-t border-gray-700'>
          <p className='text-xs text-gray-500 leading-relaxed'>
            By donating, you permanently transfer ownership of your selected NFTs to the Shitty Vault. This action cannot be reversed. Donated NFTs
            contribute to the community ecosystem and future reward distributions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationsPage;
